import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    useTheme,
} from "@mui/material";
import Axios from "axios";
import { tokens } from "../../theme";
import { getAuthHeader } from "../../utils/authToken";
import StudentEttInfoModal from "./StudentEttInfoModal";
import { StudentHeaderContent } from "./prepStudentHeader";
import { renderV2Cell, V2_CELL_LEGEND } from "./prepV2Cell";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

// Library (v2) tests (rows) × students (columns). Each cell shows the score if submitted, otherwise
// a status (in progress / not started / not assigned / scheduled). Native — no ETT.
const PrepV2TestMatrix = ({ uuid }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [infoStudent, setInfoStudent] = useState(null); // student whose ETT-email modal is open

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const res = await Axios.get(`${BASE}/${uuid}/v2-test-matrix`, {
                headers: { Authorization: getAuthHeader() },
            });
            setData(res.data);
        } catch (e) {
            console.error("Library-test matrix load error:", e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [uuid]);

    useEffect(() => {
        load();
    }, [load]);


    if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
    if (error) return <Typography color="error" sx={{ py: 2 }}>Could not load the library-test matrix.</Typography>;

    const tests = data?.tests || [];
    const students = data?.students || [];

    if (!tests.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No library tests have been added to this program.</Typography>;
    if (!students.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No students are enrolled in this program yet.</Typography>;

    const stickyHeadCell = {
        position: "sticky",
        left: 0,
        zIndex: 3,
        backgroundColor: colors.primary[600],
        width: 240,
        maxWidth: 240,
    };
    const stickyBodyCell = {
        position: "sticky",
        left: 0,
        zIndex: 1,
        backgroundColor: colors.primary[600],
        width: 240,
        maxWidth: 240,
    };

    // Per-test submitted count, for the row-leading label (students the test is assigned to).
    const submittedCount = (test) =>
        students.filter((s) => data.cells?.[s.student_id]?.[test.id]?.status === "taken").length;
    const assignedCount = (test) =>
        students.filter((s) => {
            const st = data.cells?.[s.student_id]?.[test.id]?.status;
            return st && st !== "not_assigned";
        }).length;

    return (
        <Box>
            <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600], maxHeight: "70vh", width: "fit-content", maxWidth: "100%" }}>
                <Table
                    stickyHeader
                    size="small"
                    sx={{
                        width: "auto",
                        "& .MuiTableCell-root": {
                            borderRight: `1px solid ${theme.palette.divider}`,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        "& .MuiTableCell-root:last-child": { borderRight: "none" },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...stickyHeadCell, zIndex: 4 }}>Test</TableCell>
                            {students.map((s) => (
                                <TableCell key={s.student_id} align="center" sx={{ minWidth: 130, fontWeight: 600 }}>
                                    <StudentHeaderContent student={s} onInfo={setInfoStudent} />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tests.map((test) => (
                            <TableRow key={test.id} hover>
                                <TableCell sx={stickyBodyCell}>
                                    <Typography variant="body2" fontWeight={600}>{test.test_title}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {test.course?.label ? `${test.course.label} · ` : ""}
                                        {test.due_date ? `Due ${fmtDate(test.due_date)} · ` : ""}
                                        {submittedCount(test)}/{assignedCount(test)} submitted
                                        {/* Explains scores that predate the program, and whether they count. */}
                                        {test.adopted ? ` · adopted${test.include_prior_attempts ? "" : ", prior attempts ignored"}` : ""}
                                    </Typography>
                                </TableCell>
                                {students.map((s) => (
                                    <TableCell key={s.student_id} align="center">
                                        {renderV2Cell(data.cells?.[s.student_id]?.[test.id], test)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                    <b style={{ color: theme.palette.success.main }}>18 / 20</b> = score (raw, or scaled for full exams)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    <b style={{ color: theme.palette.warning.main }}>*</b> = submitted after the due date · <b>×N</b> = best of N attempts (retakeable tests)
                </Typography>
                <Typography variant="caption" color="text.secondary">Click a score to open the student's result · {V2_CELL_LEGEND}</Typography>
            </Box>

            <StudentEttInfoModal
                open={!!infoStudent}
                onClose={() => setInfoStudent(null)}
                student={infoStudent}
            />
        </Box>
    );
};

export default PrepV2TestMatrix;
